import React, { useState, useEffect } from 'react';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import {
    Box,
    Button,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    FormControlLabel,
    Switch,
    Chip,
    Alert,
    CircularProgress,
    InputAdornment,
    Tooltip,
    Grid
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { useApiHandler } from '../../hooks/useApiHandler';
import locationService from '../../services/locationService';

function CountryManagement({ onDataChange }) {
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [openDialog, setOpenDialog] = useState(false);
    const [editingCountry, setEditingCountry] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [pagination, setPagination] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        iso_code_2: '',
        iso_code_3: '',
        address_format: '',
        postcode_required: false,
        status: true
    });
    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const { handleApiCallWithLoading, handleApiCall, showSuccess, showError } = useApiHandler();

    // Typing in the search box fetches once the user pauses, not per keystroke
    const debouncedSearchTerm = useDebouncedValue(searchTerm);

    useEffect(() => {
        loadCountries(debouncedSearchTerm);
    }, [page, rowsPerPage, debouncedSearchTerm, statusFilter]);

    const loadCountries = async (search = searchTerm) => {
        try {
            setLoading(true);
            const params = {
                page: page + 1, // Backend expects 1-based page numbers
                per_page: rowsPerPage
            };
            if (search) params.search = search;
            if (statusFilter !== 'all') params.status = statusFilter === 'active';

            const response = await locationService.getCountries(params);
            if (response.success) {
                setCountries(response.data || []);
                setTotalCount(response.pagination?.total || 0);
                setPagination(response.pagination);
            } else {
                showError(response.message || 'Error loading countries');
            }
        } catch (error) {
            console.error('Error loading countries:', error);
            if (error.response?.data?.message) {
                showError(error.response.data.message);
            } else {
                showError('Error loading countries');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        setPage(0); // Reset to first page when searching
    };

    const handleRefresh = () => {
        setSearchTerm('');
        setStatusFilter('all');
        setPage(0);
    };

    const handleOpenDialog = (country = null) => {
        if (country) {
            setEditingCountry(country);
            setFormData({
                name: country.name,
                iso_code_2: country.iso_code_2,
                iso_code_3: country.iso_code_3,
                address_format: country.address_format || '',
                postcode_required: country.postcode_required,
                status: country.status
            });
        } else {
            setEditingCountry(null);
            setFormData({
                name: '',
                iso_code_2: '',
                iso_code_3: '',
                address_format: '',
                postcode_required: false,
                status: true
            });
        }
        setFormErrors({});
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingCountry(null);
        setFormData({
            name: '',
            iso_code_2: '',
            iso_code_3: '',
            address_format: '',
            postcode_required: false,
            status: true
        });
        setFormErrors({});
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear error when user starts typing
        if (formErrors[field]) {
            setFormErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const validateForm = () => {
        const errors = {};
        
        if (!formData.name.trim()) {
            errors.name = 'Country name is required';
        }
        
        if (!formData.iso_code_2.trim()) {
            errors.iso_code_2 = 'ISO Code 2 is required';
        } else if (formData.iso_code_2.length !== 2) {
            errors.iso_code_2 = 'ISO Code 2 must be exactly 2 characters';
        }
        
        if (!formData.iso_code_3.trim()) {
            errors.iso_code_3 = 'ISO Code 3 is required';
        } else if (formData.iso_code_3.length !== 3) {
            errors.iso_code_3 = 'ISO Code 3 must be exactly 3 characters';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setSubmitting(true);
            const countryData = {
                ...formData,
                iso_code_2: formData.iso_code_2.toUpperCase(),
                iso_code_3: formData.iso_code_3.toUpperCase()
            };

            let response;
            if (editingCountry) {
                response = await locationService.updateCountry(editingCountry.country_id, countryData);
            } else {
                response = await locationService.createCountry(countryData);
            }

            if (response.success) {
                const action = editingCountry ? 'updated' : 'created';
                showSuccess(
                    `Country ${action} successfully`
                );
                handleCloseDialog();
                loadCountries();
                if (onDataChange) onDataChange();
            }
        } catch (error) {
            console.error('Error saving country:', error);
            const message = error.response?.data?.message || 'Failed to save country. Please try again.';
            showError(message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (country) => {
        if (!window.confirm(`Are you sure you want to delete "${country.name}"?`)) {
            return;
        }

        try {
            const response = await locationService.deleteCountry(country.country_id);
            if (response.success) {
                showSuccess('Country deleted successfully');
                loadCountries();
                if (onDataChange) onDataChange();
            }
        } catch (error) {
            console.error('Error deleting country:', error);
            const message = error.response?.data?.message || 'Failed to delete country. Please try again.';
            showError(message);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Server-side pagination - no need for client-side filtering
    const filteredCountries = countries;

    return (
        <Box>
            {/* Header Actions */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                    size="small"
                    placeholder="Search countries..."
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setPage(0);
                    }}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ minWidth: 250 }}
                />
                
                <FormControl size="small">
                    <TextField
                        select
                        label="Status"
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setPage(0);
                        }}
                        SelectProps={{ native: true }}
                        sx={{ minWidth: 120 }}
                    >
                        <option value="all">All</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                    </TextField>
                </FormControl>

                <Button
                    variant="outlined"
                    startIcon={<SearchIcon />}
                    onClick={handleSearch}
                >
                    Search
                </Button>

                <Tooltip title="Refresh">
                    <IconButton onClick={handleRefresh}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>

                <Box sx={{ flexGrow: 1 }} />

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                >
                    Add Country
                </Button>
            </Box>

            {/* Countries Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>ISO Code 2</TableCell>
                            <TableCell>ISO Code 3</TableCell>
                            <TableCell>Postcode Required</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>States</TableCell>
                            <TableCell>Cities</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : filteredCountries.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    No countries found
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredCountries.map((country) => (
                                <TableRow key={country.country_id}>
                                    <TableCell>{country.name}</TableCell>
                                    <TableCell>{country.iso_code_2}</TableCell>
                                    <TableCell>{country.iso_code_3}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={country.postcode_required ? 'Yes' : 'No'}
                                            color={country.postcode_required ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={country.status ? 'Active' : 'Inactive'}
                                            color={country.status ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>{country.states_count || 0}</TableCell>
                                    <TableCell>{country.cities_count || 0}</TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Edit">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenDialog(country)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={country.states_count > 0 || country.cities_count > 0 ? "Cannot delete country with existing states or cities" : "Delete"}>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDelete(country)}
                                                    disabled={country.states_count > 0 || country.cities_count > 0}
                                                    color="error"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelDisplayedRows={({ from, to, count }) => 
                        `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                    }
                />
            </TableContainer>

            {/* Add/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingCountry ? 'Edit Country' : 'Add New Country'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Country Name"
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                error={!!formErrors.name}
                                helperText={formErrors.name}
                                required
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="ISO Code 2"
                                value={formData.iso_code_2}
                                onChange={(e) => handleInputChange('iso_code_2', e.target.value.toUpperCase())}
                                error={!!formErrors.iso_code_2}
                                helperText={formErrors.iso_code_2}
                                inputProps={{ maxLength: 2 }}
                                required
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="ISO Code 3"
                                value={formData.iso_code_3}
                                onChange={(e) => handleInputChange('iso_code_3', e.target.value.toUpperCase())}
                                error={!!formErrors.iso_code_3}
                                helperText={formErrors.iso_code_3}
                                inputProps={{ maxLength: 3 }}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Address Format"
                                value={formData.address_format}
                                onChange={(e) => handleInputChange('address_format', e.target.value)}
                                multiline
                                rows={3}
                                helperText="Optional address format template"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.postcode_required}
                                        onChange={(e) => handleInputChange('postcode_required', e.target.checked)}
                                    />
                                }
                                label="Postcode Required"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.status}
                                        onChange={(e) => handleInputChange('status', e.target.checked)}
                                    />
                                }
                                label="Active Status"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={submitting}
                    >
                        {submitting ? <CircularProgress size={20} /> : (editingCountry ? 'Update' : 'Create')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default CountryManagement; 